import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["team1", "team2", "incrementBtn", "decrementBtn"]
  static values = {
    maxGames: { type: Number, default: 6 },
    isMobile: { type: Boolean, default: false }
  }

  connect() {
    // Détecter si on est sur mobile/tablette
    this.isMobileValue = window.matchMedia('(max-width: 768px)').matches

    // Auto-focus sur le premier input si sur mobile
    if (this.isMobileValue && this.hasTeam1Target) {
      // Petit délai pour laisser le temps au clavier virtuel
      setTimeout(() => {
        this.team1Target.focus()
      }, 100)
    }
  }

  // Appelé quand un champ perd le focus
  autoComplete(event) {
    const input = event.target
    const isTeam1 = input === this.team1Target
    const otherInput = isTeam1 ? this.team2Target : this.team1Target

    // Ne rien faire si le champ actuel est vide
    if (input.value === "") {
      return
    }

    const value = parseInt(input.value)
    if (isNaN(value)) {
      return
    }

    // Si l'autre champ est vide, on applique l'auto-complétion
    if (otherInput.value === "") {
      this.applyTennisRules(value, otherInput)
    }
  }

  // Applique les règles du tennis/padel
  applyTennisRules(value, otherInput) {
    const maxGames = this.maxGamesValue

    // Si le score est >= maxGames (victoire claire), l'autre a entre 0 et maxGames-2
    // On met 0 par défaut (coup de balai le plus courant)
    if (value >= maxGames) {
      otherInput.value = "0"
      return
    }

    // Si le score est exactement maxGames-1 (ex: 5 pour un set à 6)
    // L'adversaire a gagné maxGames+1 (ex: 7-5)
    if (value === maxGames - 1) {
      otherInput.value = (maxGames + 1).toString()
      return
    }

    // Si le score est < maxGames-1 (ex: 0-4 pour un set à 6)
    // L'adversaire a gagné avec maxGames (ex: 6-4)
    if (value < maxGames - 1) {
      otherInput.value = maxGames.toString()
      return
    }
  }

  // Appelé à chaque saisie pour valider les valeurs
  validateInput(event) {
    const input = event.target
    const value = parseInt(input.value)
    const maxGames = this.maxGamesValue
    const maxAllowed = maxGames + 1  // Permet tie-break à maxGames+1

    // Limiter entre 0 et maxGames+1 (pour tie-break)
    if (value < 0) {
      input.value = "0"
    } else if (value > maxAllowed) {
      input.value = maxAllowed.toString()
    }

    // Feedback visuel sur mobile
    if (this.isMobileValue) {
      this.addTouchFeedback(input)
    }
  }

  // Méthodes pour les boutons +/- sur mobile
  incrementTeam1() {
    this.incrementValue(this.team1Target)
  }

  decrementTeam1() {
    this.decrementValue(this.team1Target)
  }

  incrementTeam2() {
    this.incrementValue(this.team2Target)
  }

  decrementTeam2() {
    this.decrementValue(this.team2Target)
  }

  incrementValue(input) {
    const currentValue = parseInt(input.value) || 0
    const maxGames = this.maxGamesValue
    const maxAllowed = maxGames + 1

    if (currentValue < maxAllowed) {
      input.value = (currentValue + 1).toString()
      input.dispatchEvent(new Event('input'))
      this.addTouchFeedback(input)
    }
  }

  decrementValue(input) {
    const currentValue = parseInt(input.value) || 0

    if (currentValue > 0) {
      input.value = (currentValue - 1).toString()
      input.dispatchEvent(new Event('input'))
      this.addTouchFeedback(input)
    }
  }

  // Feedback visuel pour l'interaction tactile
  addTouchFeedback(element) {
    element.classList.add('ring-4', 'ring-blue-400')
    setTimeout(() => {
      element.classList.remove('ring-4', 'ring-blue-400')
    }, 300)
  }

  // Gérer le swipe pour navigation rapide entre sets
  handleSwipe(event) {
    if (!this.isMobileValue) return

    const touch = event.changedTouches[0]
    if (!this.touchStartX) {
      this.touchStartX = touch.screenX
      return
    }

    const swipeDistance = touch.screenX - this.touchStartX
    const swipeThreshold = 50

    // Swipe vers la gauche : focus sur set suivant
    if (swipeDistance < -swipeThreshold) {
      this.focusNextSet()
    }
    // Swipe vers la droite : focus sur set précédent
    else if (swipeDistance > swipeThreshold) {
      this.focusPreviousSet()
    }

    this.touchStartX = null
  }

  focusNextSet() {
    // Logique pour passer au set suivant
    const currentElement = document.activeElement
    const allInputs = this.element.querySelectorAll('input[type="number"]')
    const currentIndex = Array.from(allInputs).indexOf(currentElement)

    if (currentIndex < allInputs.length - 1) {
      allInputs[currentIndex + 1].focus()
    }
  }

  focusPreviousSet() {
    // Logique pour revenir au set précédent
    const currentElement = document.activeElement
    const allInputs = this.element.querySelectorAll('input[type="number"]')
    const currentIndex = Array.from(allInputs).indexOf(currentElement)

    if (currentIndex > 0) {
      allInputs[currentIndex - 1].focus()
    }
  }
}
